// Radha — currency exposure and period-end retranslation (070/071).
//
// Every figure here is shown AS BOOKED, never through the reporting-currency
// lens: this panel's whole subject is which currency the money is in, and
// translating an AED balance into EUR would erase the fact being reported.
//
// Two things a treasury/AR lead needs and could not previously see: how much
// of the book is denominated in something other than the reporting currency,
// and what the rate has done to it since those invoices were raised.
import React, { useState } from "react";

import { useFxExposure, useFxRevaluations, useRunFxRevaluation, fxErrorMessage } from "../hooks/useFx";
import { fmtMoneyNative } from "../lib/money";

export function FxPanel({ canWrite }: { canWrite: boolean }) {
  const { data: exposure } = useFxExposure();
  const { data: runs } = useFxRevaluations();
  const run = useRunFxRevaluation();
  const [asOf, setAsOf] = useState(() => new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);

  const rows = exposure ?? [];
  const base = rows[0]?.base_currency ?? "INR";
  const foreign = rows.filter((r) => r.currency !== base);
  // Nothing to say on a single-currency book — don't take up space on one.
  if (rows.length === 0 || foreign.length === 0) return null;

  const totalBase = rows.reduce((s, r) => s + Number(r.outstanding_base || 0), 0);
  const foreignBase = foreign.reduce((s, r) => s + Number(r.outstanding_base || 0), 0);
  const pct = totalBase > 0 ? Math.round((foreignBase / totalBase) * 100) : 0;
  const latest = runs?.[0];

  const go = () => {
    setNote(null);
    run.mutate(asOf, {
      onSuccess: (r) => setNote({
        ok: true,
        text: r ? `${r.line_count} invoices retranslated · ${fmtMoneyNative(r.total_unrealised, r.base_currency, false)} unrealised` : "Done",
      }),
      onError: (e) => setNote({ ok: false, text: fxErrorMessage(e) }),
    });
  };

  const th: React.CSSProperties = { padding: "6px 8px", fontWeight: 500 };
  const td: React.CSSProperties = { padding: "8px" };
  const num: React.CSSProperties = { ...td, textAlign: "right", whiteSpace: "nowrap" };

  return (
    <div className="sanctuary-card" style={{ padding: "14px 16px", marginBottom: 22 }}>
      <div style={{ font: "600 15px Inter", marginBottom: 4 }}>Currency exposure</div>
      <p style={{ color: "var(--fg-muted)", fontSize: 12, margin: "0 0 12px" }}>
        <strong style={{ color: "var(--fg)" }}>{pct}%</strong> of the open book is in a currency other than {base}.
        Balances are carried at the rate on the invoice date; retranslating them at a reporting date produces the
        unrealised exchange difference below.
      </p>

      <div className="sanctuary-scroll-x">
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ textAlign: "left", color: "var(--fg-muted)", fontSize: 11.5 }}>
              <th style={th}>Currency</th>
              <th style={{ ...th, textAlign: "right" }}>Invoices</th>
              <th style={{ ...th, textAlign: "right" }}>Outstanding</th>
              <th style={{ ...th, textAlign: "right" }}>Rate</th>
              <th style={th}>Quoted</th>
              <th style={{ ...th, textAlign: "right" }}>In {base}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.currency} style={{ borderTop: "1px solid var(--border)" }}>
                <td style={{ ...td, fontWeight: r.currency === base ? 400 : 600 }}>
                  {r.currency}
                  {r.currency === base && <span style={{ color: "var(--fg-faint)", fontWeight: 400 }}> · reporting</span>}
                </td>
                <td style={num}>{r.invoice_count}</td>
                <td style={num}>{fmtMoneyNative(r.outstanding_fc, r.currency)}</td>
                <td style={num}>{r.rate_as_of ? Number(r.rate_as_of).toFixed(4) : "—"}</td>
                <td style={{ ...td, color: "var(--fg-faint)", fontSize: 11.5 }}>{r.quote_date ?? "no quote"}</td>
                <td style={num}>{fmtMoneyNative(r.outstanding_base, base)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginTop: 12 }}>
        <span style={{ fontSize: 12.5, color: "var(--fg-muted)" }}>Revalue as at</span>
        <input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} disabled={!canWrite}
          style={{ padding: "5px 8px", border: "1px solid var(--border)", borderRadius: 7, fontSize: 12.5, background: canWrite ? "var(--surface)" : "var(--surface-2)", color: "var(--fg)" }} />
        {canWrite && (
          <button onClick={go} disabled={run.isPending}
            style={{ padding: "5px 12px", background: "var(--accent-purple)", color: "#fff", border: 0, borderRadius: 7, cursor: "pointer", fontSize: 12.5 }}>
            {run.isPending ? "Revaluing…" : "Run revaluation"}
          </button>
        )}
        {note && <span style={{ fontSize: 12.5, color: note.ok ? "var(--status-good)" : "var(--status-critical)" }}>{note.text}</span>}
      </div>

      {latest && (
        <div style={{ marginTop: 10, fontSize: 12.5, color: "var(--fg-muted)" }}>
          Last run {latest.as_of_date} · {latest.line_count} invoices ·{" "}
          <strong style={{ color: Number(latest.total_unrealised) >= 0 ? "var(--status-good)" : "var(--status-critical)" }}>
            {fmtMoneyNative(latest.total_unrealised, latest.base_currency, false)}
          </strong>{" "}
          unrealised {Number(latest.total_unrealised) >= 0 ? "gain" : "loss"}
          <span style={{ color: "var(--fg-faint)" }}> — an accounting restatement, not cash. Realised differences arise on settlement.</span>
        </div>
      )}
    </div>
  );
}
